import type React from "react";
import { UniqueIdentifier } from "@dnd-kit/core";
import { FormInstance } from "antd";
import { TableRowSelection } from "antd/lib/table/interface";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { CheckboxValueType } from "antd/lib/checkbox/Group";

export enum InputType {
  Text,
  Number,
  Switch,
}

export enum EditableTableMode {
  View,
  Edit,
}

export type ThumbnailSize = "small" | "medium" | "large";

export type ColumnType =
  | "thumbnail"
  | "plain"
  | "text"
  | "arrayString"
  | "number"
  | "status"
  | "date";

export interface ColumnConfig<T = unknown> {
  /** Field key. Also used as the stable column id source. */
  key: string;
  /** Header label, also shown in the show/hide-columns popover. */
  title: string;
  /**
   * Header tooltip content. Defaults to `title`. Set `false` to disable the
   * tooltip (e.g. when the header already fits).
   */
  tooltip?: React.ReactNode | false;
  /** antd dataIndex. Defaults to `key`. */
  dataIndex?: string;
  type: ColumnType;
  /** Cell is editable while in edit mode. */
  editable?: boolean;
  /** Build dynamic column filters from the data (string or arrayString columns). */
  filterable?: boolean;
  /** Show the column sorter. Defaults to false; set true to enable sorting. */
  sortable?: boolean;
  /** Fixed column width (px). Defaults by type. Needed for horizontal scroll. */
  width?: number;
  /** Custom cell renderer. Overrides the type-based render when provided. */
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
}

export interface DragIndexState {
  active: UniqueIdentifier;
  over: UniqueIdentifier | undefined;
  direction?: "left" | "right";
}

export type DisplayColumns = Record<string, boolean>;

/** Return shape of `useColumnVisibility`, owned by the page. */
export interface ColumnVisibilityState {
  visibility: Partial<DisplayColumns>;
  indeterminate: boolean;
  checkAll: boolean;
  options: string[];
  values: CheckboxValueType[];
  onCheckAllChange: (e: CheckboxChangeEvent) => void;
  onDisplayColumnChanged: (list: CheckboxValueType[]) => void;
}

/** Return shape of `useInlineEdit`, owned by the page. */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface InlineEditState<T extends object> {
  form: FormInstance;
  isEditing: boolean;
  onButtonEditClick: () => Promise<void>;
  onButtonCancelClick: () => Promise<void>;
  rowSelection: () => TableRowSelection<T> | null;
  onFormValueChanges: (changesValue: Record<string, unknown>) => void;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export interface DataTableProps<T extends object> {
  rowKey: string;
  dataSource: T[];
  columnConfigs: ColumnConfig<T>[];
  loading?: boolean;
  columnToggle?: boolean;
  editable?: boolean;
  pagination?: boolean;
  thumbnailSize?: ThumbnailSize;
  visibility?: ColumnVisibilityState;
  edit?: InlineEditState<T>;
  rowDnd?: boolean;
  onRowReorder?: (from: number, to: number) => void;
  rowClassName?: (record: T, index: number) => string;
}
